// src/pages/EventReports.jsx
// Cross-event reporting overlay for the Events tool. Pulls an aggregated report
// from the getEventsReport Cloud Function and renders two tabs:
//   • By event   — signed up vs showed up vs no-show vs on-bus, per trip.
//   • By student — scoreboard: sign-ups, attended, no-shows, no-show rate, and
//                  each kid's full trip history (expand a row).
// Both tabs export CSV (for sorting/scorekeeping in Sheets).
//
// Rendered by EventManager when the manager clicks "Reports". Scope is decided
// server-side: admins/superadmin see all trips; a plain manager sees their own.

import React, { useEffect, useMemo, useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";

const KA_RED = "#E83530";
const KA_NAVY = "#233C5F";
const KA_GOLD = "#A29164";
const GREEN = "#1a7f37";

function fmtDate(ms) {
  if (!ms) return "—";
  try {
    return new Date(ms).toLocaleString("en-US", {
      timeZone: "Asia/Amman", month: "short", day: "numeric", year: "numeric",
    });
  } catch { return "—"; }
}

function pct(x) {
  return `${Math.round((x || 0) * 100)}%`;
}

function downloadCsv(filename, rows) {
  const esc = (v) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = rows.map((r) => r.map(esc).join(",")).join("\r\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function EventReports({ onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("events"); // events | students
  const [q, setQ] = useState("");
  const [sortKey, setSortKey] = useState("noShow"); // noShow | attended | signedUp | name
  const [openKey, setOpenKey] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const fns = getFunctions(undefined, "us-central1");
        const { data } = await httpsCallable(fns, "getEventsReport")({});
        setData(data);
      } catch (e) {
        setError(e?.message || "Could not load the report.");
      }
    })();
  }, []);

  const students = useMemo(() => {
    if (!data?.students) return [];
    const needle = q.trim().toLowerCase();
    let list = data.students;
    if (needle) {
      list = list.filter(
        (s) =>
          (s.name || "").toLowerCase().includes(needle) ||
          (s.email || "").includes(needle) ||
          (s.gradYear || "").includes(needle)
      );
    }
    const sorters = {
      noShow: (a, b) => b.noShow - a.noShow || a.name.localeCompare(b.name),
      signedIn: (a, b) => b.signedIn - a.signedIn || a.name.localeCompare(b.name),
      signedUp: (a, b) => b.signedUp - a.signedUp || a.name.localeCompare(b.name),
      name: (a, b) => a.name.localeCompare(b.name),
    };
    return [...list].sort(sorters[sortKey] || sorters.noShow);
  }, [data, q, sortKey]);

  const exportEvents = () => {
    const rows = [["Event", "Date", "Signed up", "Signed in", "No-show", "On bus", "Capacity", "Status"]];
    (data?.events || []).forEach((e) => {
      rows.push([
        e.name, fmtDate(e.startAt), e.signedUp, e.signedIn, e.noShow, e.boarded,
        e.capacity ?? "", e.started ? "Past" : "Upcoming",
      ]);
    });
    downloadCsv("events-report.csv", rows);
  };

  const exportStudents = () => {
    const rows = [["Name", "Email", "Grad year", "Role", "Signed up", "Signed in", "No-shows", "No-show %"]];
    (data?.students || []).forEach((s) => {
      rows.push([
        s.name, s.email, s.gradYear || "", s.role, s.signedUp, s.signedIn, s.noShow, pct(s.noShowRate),
      ]);
    });
    downloadCsv("student-scoreboard.csv", rows);
  };

  const totals = useMemo(() => {
    const ev = data?.events || [];
    const signedUp = ev.reduce((a, e) => a + (e.signedUp || 0), 0);
    const signedIn = ev.reduce((a, e) => a + (e.signedIn || 0), 0);
    const noShow = ev.reduce((a, e) => a + (e.noShow || 0), 0);
    return { events: ev.length, signedUp, signedIn, noShow };
  }, [data]);

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ margin: 0, color: KA_NAVY }}>
            Trip Reports {data?.scope === "mine" ? "— your trips" : "— all trips"}
          </h3>
          <button onClick={onClose} style={ghost}>Close</button>
        </div>

        {!data && !error && <p style={{ color: "#888" }}>Loading report…</p>}
        {error && <p style={{ color: KA_RED }}>{error}</p>}

        {data && (
          <>
            {/* Summary tiles */}
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", margin: "12px 0 6px" }}>
              <Tile label="Trips" value={totals.events} />
              <Tile label="Signed up" value={totals.signedUp} />
              <Tile label="Signed in" value={totals.signedIn} color={GREEN} />
              <Tile label="No-shows" value={totals.noShow} color={KA_RED} />
            </div>

            {/* Tabs */}
            <div style={{ display: "flex", gap: 8, margin: "8px 0 12px" }}>
              <button onClick={() => setTab("events")} style={tab === "events" ? tabOn : tabOff}>By event</button>
              <button onClick={() => setTab("students")} style={tab === "students" ? tabOn : tabOff}>By student</button>
            </div>

            {tab === "events" && (
              <>
                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
                  <button onClick={exportEvents} style={ghost}>Export CSV</button>
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table style={table}>
                    <thead>
                      <tr>
                        <Th>Event</Th><Th>Date</Th><Th center>Signed up</Th><Th center>Signed in</Th>
                        <Th center>No-show</Th><Th center>On bus</Th><Th center>Cap</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.events.length === 0 && (
                        <tr><td colSpan={7} style={{ ...td, color: "#999" }}>No trips yet.</td></tr>
                      )}
                      {data.events.map((e) => (
                        <tr key={e.id}>
                          <td style={td}>
                            {e.name}
                            {!e.started && <span style={pill}>upcoming</span>}
                          </td>
                          <td style={td}>{fmtDate(e.startAt)}</td>
                          <td style={tdC}>{e.signedUp}</td>
                          <td style={{ ...tdC, color: GREEN, fontWeight: 600 }}>{e.signedIn}</td>
                          <td style={{ ...tdC, color: e.noShow ? KA_RED : "#999", fontWeight: e.noShow ? 600 : 400 }}>
                            {e.started ? e.noShow : "—"}
                          </td>
                          <td style={tdC}>{e.boarded}</td>
                          <td style={tdC}>{e.capacity ?? "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p style={{ fontSize: 12, color: "#999", marginTop: 10 }}>
                  <strong>Signed up</strong> = pre-registered on the sign-up page. <strong>Signed in</strong> = scanned or
                  marked present (includes walk-ups who never pre-registered). No-shows are pre-registered students who never signed in.
                </p>
              </>
            )}

            {tab === "students" && (
              <>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 8 }}>
                  <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search students…"
                    style={{ ...inp, flex: "1 1 200px" }}
                  />
                  <select value={sortKey} onChange={(e) => setSortKey(e.target.value)} style={sel}>
                    <option value="noShow">Sort: most no-shows</option>
                    <option value="signedIn">Sort: most signed in</option>
                    <option value="signedUp">Sort: most sign-ups</option>
                    <option value="name">Sort: name</option>
                  </select>
                  <button onClick={exportStudents} style={ghost}>Export CSV</button>
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table style={table}>
                    <thead>
                      <tr>
                        <Th>Student</Th><Th center>Grade</Th><Th center>Signed up</Th>
                        <Th center>Signed in</Th><Th center>No-shows</Th><Th center>No-show %</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {students.length === 0 && (
                        <tr><td colSpan={6} style={{ ...td, color: "#999" }}>No students yet.</td></tr>
                      )}
                      {students.map((s) => (
                        <React.Fragment key={s.key}>
                          <tr onClick={() => setOpenKey(openKey === s.key ? null : s.key)} style={{ cursor: "pointer" }}>
                            <td style={td}>
                              <span style={{ color: KA_NAVY }}>{s.name}</span>
                              {s.role === "staff" && <span style={{ ...pill, background: "rgba(162,145,100,0.2)", color: "#7a6a3f" }}>teacher</span>}
                              <span style={{ color: "#bbb", fontSize: 11, marginLeft: 6 }}>{openKey === s.key ? "▲" : "▼"}</span>
                            </td>
                            <td style={tdC}>{s.gradYear || "—"}</td>
                            <td style={tdC}>{s.signedUp}</td>
                            <td style={{ ...tdC, color: GREEN, fontWeight: 600 }}>{s.signedIn}</td>
                            <td style={{ ...tdC, color: s.noShow ? KA_RED : "#999", fontWeight: s.noShow ? 700 : 400 }}>{s.noShow}</td>
                            <td style={{ ...tdC, color: s.noShowRate >= 0.34 ? KA_RED : "#555" }}>{pct(s.noShowRate)}</td>
                          </tr>
                          {openKey === s.key && (
                            <tr>
                              <td colSpan={6} style={{ ...td, background: "#fafafa" }}>
                                <div style={{ display: "grid", gap: 4 }}>
                                  {s.trips.map((t, idx) => (
                                    <div key={idx} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 13 }}>
                                      <span style={{ color: KA_NAVY }}>{t.name}</span>
                                      <span style={{ display: "flex", gap: 10, alignItems: "center" }}>
                                        <span style={{ color: "#999", fontSize: 12 }}>{fmtDate(t.startAt)}</span>
                                        <TripBadge t={t} />
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p style={{ fontSize: 12, color: "#999", marginTop: 10 }}>
                  No-show % is of past commitments only (attended + no-shows). Upcoming trips don't count against anyone.
                </p>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function TripBadge({ t }) {
  let label = t.status;
  let color = "#777";
  let bg = "#eee";
  if (!t.started && (t.status === "confirmed" || t.status === "waitlisted")) {
    label = t.status === "waitlisted" ? "waitlist (upcoming)" : "upcoming";
    color = KA_GOLD; bg = "rgba(162,145,100,0.18)";
  } else if (t.boarded) {
    label = "on bus"; color = GREEN; bg = "rgba(26,127,55,0.12)";
  } else if (t.present) {
    label = "attended"; color = GREEN; bg = "rgba(26,127,55,0.12)";
  } else if (t.noShow) {
    label = "no-show"; color = KA_RED; bg = "rgba(232,53,48,0.1)";
  } else if (t.status === "waitlisted") {
    label = "waitlist"; color = KA_GOLD; bg = "rgba(162,145,100,0.18)";
  }
  return (
    <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3, padding: "2px 8px", borderRadius: 6, color, background: bg }}>
      {label}
    </span>
  );
}

function Tile({ label, value, color }) {
  return (
    <div style={{ flex: "1 1 110px", background: "#fafafa", border: "1px solid #eee", borderRadius: 10, padding: "10px 14px" }}>
      <div style={{ fontSize: 22, fontWeight: 700, color: color || KA_NAVY }}>{value}</div>
      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, color: "#888" }}>{label}</div>
    </div>
  );
}

function Th({ children, center }) {
  return (
    <th style={{ textAlign: center ? "center" : "left", fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4, color: "#888", padding: "6px 10px", borderBottom: "2px solid #eee", whiteSpace: "nowrap" }}>
      {children}
    </th>
  );
}

const overlay = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 210 };
const modal = { background: "#fff", borderRadius: 14, padding: 20, width: "100%", maxWidth: 860, maxHeight: "88vh", overflowY: "auto", boxShadow: "0 12px 40px rgba(0,0,0,0.2)" };
const ghost = { background: "#fff", color: KA_NAVY, border: `1px solid ${KA_NAVY}`, borderRadius: 8, padding: "8px 14px", fontSize: 13, cursor: "pointer" };
const inp = { padding: "9px 12px", border: "1px solid #ccc", borderRadius: 8, fontSize: 15, boxSizing: "border-box" };
const sel = { padding: "9px 10px", border: "1px solid #ccc", borderRadius: 8, fontSize: 13, background: "#fff", cursor: "pointer" };
const table = { width: "100%", borderCollapse: "collapse", fontSize: 14 };
const td = { padding: "8px 10px", borderBottom: "1px solid #f0f0f0", color: "#333" };
const tdC = { ...td, textAlign: "center" };
const pill = { fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3, padding: "2px 6px", borderRadius: 6, background: "rgba(162,145,100,0.18)", color: "#7a6a3f", marginLeft: 8 };
const tabOn = { background: KA_NAVY, color: "#fff", border: `1px solid ${KA_NAVY}`, borderRadius: 20, padding: "6px 18px", fontSize: 13, fontWeight: 600, cursor: "pointer" };
const tabOff = { background: "#fff", color: "#666", border: "1px solid #ccc", borderRadius: 20, padding: "6px 18px", fontSize: 13, fontWeight: 600, cursor: "pointer" };